import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ReviewCard from "../Utils/Cards/review-card-component";
import ElegantReviewCard from '../Utils/Cards/review-card-elegant-component';
import { Helmet } from 'react-helmet';

function ReviewCardItem({ review, useElegant }) {
  return useElegant ? (
    <ElegantReviewCard
      name={review.name}
      reviewText={review.reviewText}
      shortText={review.shortText}
      profile={review.profile}
      image={review.image}
    />
  ) : (
    <ReviewCard
      name={review.name}
      reviewText={review.reviewText}
      shortText={review.shortText}
      image={review.image}
      reviewDate={review.reviewDate}
    />
  );
}

const ReviewList = ({ reviews, useElegant = false, seoTag = true }) => {
  const businessInfo = useSelector(state => state.systemData?.businessInfo);
  const scrollRef = useRef(null);
  // Auto-scroll state: paused on hover/drag, direction flips at the ends.
  const pausedRef = useRef(false);
  const directionRef = useRef(1); // 1 = forward, -1 = back
  // Tracks whether the last pointer interaction was a drag (vs a click), so a
  // drag-release doesn't trigger a card click (e.g. the "Read more" modal).
  const didDrag = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startScrollLeft, setStartScrollLeft] = useState(0);

  // ── Auto-scroll, creeps the row, ping-pongs at the ends, pauses on hover/drag/touch ──
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    const interval = setInterval(() => {
      if (pausedRef.current) return;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll <= 0) return; // nothing to scroll
      el.scrollLeft += directionRef.current;
      if (directionRef.current === 1 && el.scrollLeft >= maxScroll - 1) {
        directionRef.current = -1;
      } else if (directionRef.current === -1 && el.scrollLeft <= 1) {
        directionRef.current = 1;
      }
    }, 20);

    const pause = () => { pausedRef.current = true; };
    const resume = () => { pausedRef.current = false; };
    el.addEventListener('touchstart', pause, { passive: true });
    el.addEventListener('touchend', resume, { passive: true });

    return () => {
      clearInterval(interval);
      el.removeEventListener('touchstart', pause);
      el.removeEventListener('touchend', resume);
    };
  }, [reviews]);

  // ── Drag-to-scroll (desktop mouse). Touch uses native overflow scrolling. ──
  const handleMouseEnter = () => { pausedRef.current = true; };
  const handleMouseLeaveArea = () => { pausedRef.current = false; setIsDragging(false); };

  const handleMouseDown = (e) => {
    const el = scrollRef.current;
    if (!el) return;
    didDrag.current = false;
    pausedRef.current = true;
    setIsDragging(true);
    setStartX(e.pageX - el.offsetLeft);
    setStartScrollLeft(el.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const el = scrollRef.current;
    if (!el) return;
    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    const walk = (x - startX) * 1.5; // scroll speed multiplier
    if (Math.abs(walk) > 4) didDrag.current = true;
    el.scrollLeft = startScrollLeft - walk;
  };

  const handleMouseUp = () => setIsDragging(false);

  // Swallow the click that fires right after a drag so cards don't react to it.
  const handleClickCapture = (e) => {
    if (didDrag.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  if (!reviews?.length) return null;

  return (
    <>
      {seoTag && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'LocalBusiness',
              name: businessInfo?.businessName,
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: '5',
                bestRating: '5',
                reviewCount: reviews.length,
              },
              review: reviews.map(r => ({
                '@type': 'Review',
                author: { '@type': 'Person', name: r.name },
                reviewBody: r.reviewText,
                datePublished: r.reviewDate,
                reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
              })),
            })}
          </script>
        </Helmet>
      )}

      <div
        ref={scrollRef}
        dir="ltr"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeaveArea}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onClickCapture={handleClickCapture}
        className={`flex gap-4 overflow-x-auto py-4 px-4 cursor-grab [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${isDragging ? 'cursor-grabbing select-none' : ''}`}
      >
        {reviews.map((review, i) => (
          <div key={i} className="shrink-0">
            <ReviewCardItem review={review} useElegant={useElegant} />
          </div>
        ))}
      </div>
    </>
  );
};

export default ReviewList;
